import { useEffect } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { cx } from '@emotion/css';
import { Card, Col, Row, Space } from 'antd';
import { Formik } from 'formik';
import getCanvas from 'html2canvas';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addOfferOrderIntent, doClearUpdateAccountSignature, doUpdateAccountSignature } from 'src/actions';
import { ErrorResponseDto, OfferOrderSecurityDeliveryOptionDto } from 'src/dtos';
import { useSubmittedAccountSelector } from 'src/hooks';
import { MCheckbox, MFormTextField, MButton, MAlert, UnknownErrorMessage } from 'src/lib';
import { OfferOrderIntent, OfferDetails } from 'src/models';
import { UserSignatureOptionList } from 'src/pages/PortfolioCompletion/SignApplication/UserSignatureOptionList';
import { BaseStyle, Images, Spacing } from 'src/styles';
import { Maybe, isRegDTypeCOffer } from 'src/utils';

import { NewOfferOrderLayout } from '../../../../../lib/Layout/NewOfferOrderLayout/NewOfferOrderLayout';
import { ThisOfferOrderWillDisclaimer } from '../../../../../lib/ThisOfferOrderWillDisclaimer/ThisOfferOrderWillDisclaimer';
import { DOCUMENT_TYPES } from '../../../../Offers/components/OfferDocumentsTab/constants';
import { DETAILS_PATH } from '../NewOfferOrderConfirmStep/NewOfferOrderConfirmStep';
import { SECURITY_DELIVERY_INFORMATION } from '../NewOfferOrderDeliveryInformationStep/NewOfferOrderSecurityDeliveryInformationStep';
import * as Styles from '../NewOfferOrders.styles';

export const DOCUMENTS_PATH = 'documents';

interface NewOfferOrderDocumentsFormValues {
  areDocumentsReviewed: boolean;
  primaryAccountHolderSignature?: HTMLElement;
  secondaryAccountHolderSignature?: HTMLElement;
}

export const NewOfferOrderDocumentsStep = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const offerDetails: OfferDetails = useSelector((state: any) => state.offers.offerDetails?.data);
  const updateSignatureSucceeded: boolean = useSelector((state: any) =>
    Boolean(state.accounts.updateSignature.__succeeded),
  );
  const isUpdateSignatureLoading: boolean = useSelector((state: any) => state.accounts.updateSignature.isLoading);
  const updateSignatureError: Maybe<ErrorResponseDto> = useSelector(
    (state: any) => state.accounts.updateSignature.error,
  );
  const offerOrderIntent: OfferOrderIntent = useSelector((state: any) => state.offerOrders.intent);

  const { account } = useSubmittedAccountSelector();

  const findDeliveryOptionIfFound = (): Maybe<OfferOrderSecurityDeliveryOptionDto> => {
    if (offerOrderIntent.hasManySecurityDeliveryOptions) {
      return;
    }
    const deliveryOption = offerDetails?.externalDetails?.securityDeliveryOptions?.[0];

    if (deliveryOption) {
      return {
        label: deliveryOption.label,
        tooltip: deliveryOption.tooltip,
      };
    }
  };

  const getAreDocumentsReviewedDisclaimer = () => {
    if (isRegDTypeCOffer(offerDetails)) {
      return (
        'By clicking “Continue” I have reviewed the above offering documents and understand there are risks when investing. ' +
        'Once your order is placed, a representative from My IPO will contact you with instructions on how to complete ' +
        'the subscription agreement and any other documents needed to complete your Offer Order.'
      );
    }

    return offerDetails.subscriptionAgreementId
      ? 'I have reviewed the above offering documents and understand there are risks when investing. By clicking "Continue" I am electronically signing the offering documents.'
      : 'By clicking “Continue” I have reviewed the above offering documents and understand there are risks when investing.';
  };

  const shouldDisplaySignatureInput = () => {
    return !account.primaryAccountHolder?.signature;
  };

  const shouldDisplaySecondarySignatureInput = () => {
    if (account.type.isJoint) {
      return !account.secondaryAccountHolder?.signature;
    }

    if (account.type.isEntity) {
      return (
        account.entityAccountType?.isTrust &&
        account.secondaryAccountHolder &&
        !account.secondaryAccountHolder.signature
      );
    }

    return false;
  };

  const shouldDisplaySignature = () => !isRegDTypeCOffer(offerDetails) && account.primaryAccountHolder?.signature;

  const shouldDisplaySecondarySignature = () =>
    account.type.isJoint || (account.entityAccountType?.isTrust && account.secondaryAccountHolder);

  const getSignatureLabel = ({ isPrimary }: { isPrimary?: boolean }): Maybe<string> => {
    if (isPrimary) {
      if (account.type.isJoint) {
        return 'Primary Account Holder Signature';
      }

      if (account.type.isEntity) {
        return 'Authorized Signer 1 Signature';
      }

      return 'Signature';
    }

    if (account.type.isJoint) {
      return 'Joint Account Holder Signature';
    }

    if (account.type.isEntity) {
      return 'Authorized Signer 2 Signature';
    }
  };

  const goToNextPage = () => {
    dispatch(
      addOfferOrderIntent({
        ...offerOrderIntent,
        areDocumentsReviewed: true,
        securityDeliveryOption: findDeliveryOptionIfFound(),
      }),
    );
    window.gtag('event', 'offer_order_review_documents_complete', {
      offer_id: offerDetails.id,
      offer_name: offerDetails.name,
      account_id: account.accountId,
    });

    const nextPage =
      offerDetails.isExternalOffer && offerOrderIntent.hasManySecurityDeliveryOptions
        ? `../${SECURITY_DELIVERY_INFORMATION}`
        : `../${DETAILS_PATH}`;
    navigate(`${nextPage}`);
  };

  useEffect(() => {
    if (updateSignatureSucceeded) {
      goToNextPage();

      return () => {
        dispatch(doClearUpdateAccountSignature());
      };
    }
  }, [updateSignatureSucceeded]);

  return (
    <NewOfferOrderLayout
      step='documents'
      description='Please review the information below before completing the order process.'
      hasManySecurityDeliveryOptions={offerOrderIntent.hasManySecurityDeliveryOptions}
      offerId={offerDetails.id}>
      <Formik<NewOfferOrderDocumentsFormValues>
        enableReinitialize
        initialValues={{
          areDocumentsReviewed: offerOrderIntent.areDocumentsReviewed,
        }}
        validate={(values: NewOfferOrderDocumentsFormValues) => {
          const errors: Partial<{
            areDocumentsReviewed: string;
            primaryAccountHolderSignature: string;
            secondaryAccountHolderSignature: string;
          }> = {};

          if (!values.areDocumentsReviewed) {
            errors.areDocumentsReviewed = 'Field must be checked';
          }

          if (shouldDisplaySignatureInput() && !values.primaryAccountHolderSignature) {
            errors.primaryAccountHolderSignature = 'Signature is required';
          }

          if (shouldDisplaySecondarySignatureInput() && !values.secondaryAccountHolderSignature) {
            errors.secondaryAccountHolderSignature = 'Signature is required';
          }

          return errors;
        }}
        onSubmit={async values => {
          let primaryAccountHolderSignature: Maybe<string>;
          let secondaryAccountHolderSignature: Maybe<string>;

          if (shouldDisplaySignatureInput() && values.primaryAccountHolderSignature) {
            primaryAccountHolderSignature = await getCanvas(values.primaryAccountHolderSignature).then(canvas => {
              return canvas.toDataURL();
            });
          }

          if (shouldDisplaySecondarySignatureInput() && values.secondaryAccountHolderSignature) {
            secondaryAccountHolderSignature = await getCanvas(values.secondaryAccountHolderSignature).then(canvas => {
              return canvas.toDataURL();
            });
          }

          if (primaryAccountHolderSignature) {
            dispatch(
              doUpdateAccountSignature({
                params: {
                  id: account.id,
                },
                body: {
                  primaryAccountHolder: {
                    signature: primaryAccountHolderSignature,
                  },
                  secondaryAccountHolder: secondaryAccountHolderSignature
                    ? {
                        signature: secondaryAccountHolderSignature,
                      }
                    : undefined,
                },
              }),
            );

            return;
          }

          goToNextPage();
        }}>
        {form => {
          return (
            <>
              <Col span={24} className={cx(BaseStyle.level, Spacing.mb40)}>
                <img
                  className={Styles.offerLogo}
                  src={offerDetails.logoUrl || Images.DefaultOffering}
                  alt={`${offerDetails.name}_logo`}
                />
                <span className={Styles.offerName}>{offerDetails.name}</span>
              </Col>
              <Row className={Spacing.mb24}>
                {offerDetails?.documents.map(document => (
                  <Col span={24} key={document.id}>
                    <MFormTextField
                      label={DOCUMENT_TYPES[document.type].label}
                      value={
                        <MButton type='secondary' href={document.url} target='_blank'>
                          View
                        </MButton>
                      }
                    />
                  </Col>
                ))}
                {offerOrderIntent.isConditional && (
                  <ThisOfferOrderWillDisclaimer
                    min={offerDetails.minSharePrice}
                    max={offerDetails.maxSharePrice}
                    className={Spacing.mb12}
                  />
                )}
                <Col span={24}>
                  <MCheckbox
                    checked={form.values.areDocumentsReviewed}
                    onChange={value => form.setFieldValue('areDocumentsReviewed', value.checked)}
                    error={form.errors.areDocumentsReviewed}
                    testId={'offer-order-check-sign-documents'}>
                    {getAreDocumentsReviewedDisclaimer()}
                  </MCheckbox>
                </Col>
                {shouldDisplaySignatureInput() && (
                  <>
                    <Col span={24}>
                      <UserSignatureOptionList
                        title={getSignatureLabel({ isPrimary: true })}
                        fullName={`${account.primaryAccountHolder?.firstName} ${account.primaryAccountHolder?.lastName}`}
                        value={form.values.primaryAccountHolderSignature}
                        error={form.errors.primaryAccountHolderSignature}
                        onChange={signature => {
                          form.setFieldValue('primaryAccountHolderSignature', signature);
                        }}
                        testId={'primary'}
                      />
                    </Col>
                    {shouldDisplaySecondarySignatureInput() && (
                      <Col span={24} className={Spacing.mt16}>
                        <UserSignatureOptionList
                          title={getSignatureLabel({ isPrimary: false })}
                          fullName={`${account.secondaryAccountHolder?.firstName} ${account.secondaryAccountHolder?.lastName}`}
                          value={form.values.secondaryAccountHolderSignature}
                          error={form.errors.secondaryAccountHolderSignature}
                          onChange={signature => {
                            form.setFieldValue('secondaryAccountHolderSignature', signature);
                          }}
                          testId={'joint'}
                        />
                      </Col>
                    )}
                  </>
                )}
                {shouldDisplaySignature() && (
                  <>
                    <Col span={24} data-testid={'offer-order-documents-primary-signature-text-and-image'}>
                      <div className={Styles.sectionTitle}>{getSignatureLabel({ isPrimary: true })}</div>
                      {account.primaryAccountHolder?.signature && (
                        <Card className={Styles.signatureWrapper}>
                          <img
                            src={account.primaryAccountHolder.signature}
                            alt='signature'
                            className={Styles.signature}
                          />
                        </Card>
                      )}
                    </Col>
                    {shouldDisplaySecondarySignature() && (
                      <Col span={24} data-testid={'offer-order-documents-joint-signature-text-and-image'}>
                        <div className={Styles.sectionTitle}>{getSignatureLabel({ isPrimary: false })}</div>
                        {account.secondaryAccountHolder?.signature && (
                          <Card className={Styles.signatureWrapper}>
                            <img
                              src={account.secondaryAccountHolder.signature}
                              alt='signature'
                              className={Styles.signature}
                            />
                          </Card>
                        )}
                      </Col>
                    )}
                  </>
                )}
                {updateSignatureError && (
                  <Col span={24} className={Spacing.my16}>
                    <MAlert
                      type='error'
                      description={
                        updateSignatureError?.message ?? <UnknownErrorMessage message={updateSignatureError?.message} />
                      }
                      icon={<ExclamationCircleOutlined />}
                      showIcon
                      closable={false}
                    />
                  </Col>
                )}
                <Col
                  span={24}
                  className={cx(BaseStyle.level, Spacing.mt24)}
                  data-testid={'offer-documents-disclosures-row-btns'}>
                  <Space direction='horizontal'>
                    <MButton type='tertiary' onClick={() => navigate(-1)} disabled={isUpdateSignatureLoading}>
                      Back
                    </MButton>
                    <MButton
                      onClick={() => form.submitForm()}
                      loading={isUpdateSignatureLoading}
                      disabled={isUpdateSignatureLoading || !form.isValid}>
                      Continue
                    </MButton>
                  </Space>
                </Col>
              </Row>
            </>
          );
        }}
      </Formik>
    </NewOfferOrderLayout>
  );
};
